import { TypedEvent } from '../typed_event.js';
import en from './locales/en.json';
import zhCN from './locales/zh-CN.json';

export type Locale = 'en' | 'zh-CN';

export interface TranslationMessages {
	[key: string]: string | TranslationMessages;
}

export interface I18nConfig {
	locale?: Locale;
	fallbackLocale?: Locale;
}

const LOCALES: Record<Locale, TranslationMessages> = {
	'en': en as TranslationMessages,
	'zh-CN': zhCN as TranslationMessages,
};

const STORAGE_KEY = 'wotlk_ui_locale';

/**
 * I18nManager - Manages internationalization for the UI
 *
 * Provides:
 * - Language state management with localStorage persistence
 * - Translation function with dot-notation namespace support
 * - Automatic browser language detection
 * - Fallback to English for missing translations
 * - Event emission on locale change for reactive updates
 */
export class I18nManager {
	private currentLocale: Locale;
	private fallbackLocale: Locale;
	readonly localeChangeEmitter = new TypedEvent<Locale>();

	constructor(config: I18nConfig = {}) {
		this.fallbackLocale = config.fallbackLocale || 'en';
		this.currentLocale = this.detectLocale(config.locale);
	}

	/**
	 * Detects the appropriate locale based on browser settings or stored preference
	 */
	private detectLocale(preferredLocale?: Locale): Locale {
		// Check localStorage first - but only if it's a valid locale
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored && this.isValidLocale(stored)) {
			return stored as Locale;
		}
		// Clear any invalid stored value
		if (stored) {
			localStorage.removeItem(STORAGE_KEY);
		}

		// Use provided locale if valid
		if (preferredLocale && this.isValidLocale(preferredLocale)) {
			return preferredLocale;
		}

		// Default to Chinese
		return 'zh-CN';
	}

	private isValidLocale(locale: string): locale is Locale {
		return locale === 'en' || locale === 'zh-CN';
	}

	/**
	 * Gets the current locale
	 */
	getLocale(): Locale {
		return this.currentLocale;
	}

	/**
	 * Sets the current locale and persists it to localStorage
	 */
	setLocale(eventId: number, locale: Locale): void {
		if (!this.isValidLocale(locale)) {
			console.warn(`Invalid locale: ${locale}, falling back to English`);
			locale = 'en';
		}

		if (this.currentLocale !== locale) {
			this.currentLocale = locale;
			localStorage.setItem(STORAGE_KEY, locale);
			this.localeChangeEmitter.emit(eventId, locale);
		}
	}

	/**
	 * Translates a key using the current locale
	 * Supports dot notation for nested keys (e.g., 'sim.iterations')
	 * @param key - Translation key with dot notation
	 * @param params - Optional parameters for string interpolation
	 * @returns Translated string or the key if not found
	 */
	t(key: string, params?: Record<string, string | number>): string {
		const message = this.getTranslation(this.currentLocale, key);
		const translated = message || this.getTranslation(this.fallbackLocale, key) || key;

		// Handle parameter interpolation
		if (params) {
			return this.interpolate(translated, params);
		}

		return translated;
	}

	/**
	 * Gets a translation message for a specific locale
	 */
	private getTranslation(locale: Locale, key: string): string | null {
		const keys = key.split('.');
		let result: any = LOCALES[locale];

		for (const k of keys) {
			if (result && typeof result === 'object' && k in result) {
				result = result[k];
			} else {
				return null;
			}
		}

		return typeof result === 'string' ? result : null;
	}

	/**
	 * Interpolates parameters into a translation string
	 * Supports {{param}} syntax
	 */
	private interpolate(message: string, params: Record<string, string | number>): string {
		return message.replace(/\{\{(\w+)\}\}/g, (match, key) => {
			return params[key]?.toString() || match;
		});
	}

	/**
	 * Checks if a translation key exists
	 */
	has(key: string): boolean {
		return this.getTranslation(this.currentLocale, key) !== null;
	}

	/**
	 * Gets all available locales
	 */
	getAvailableLocales(): Locale[] {
		return Object.keys(LOCALES) as Locale[];
	}

	/**
	 * Gets the display name for a locale
	 */
	getLocaleName(locale: Locale): string {
		const names: Record<Locale, string> = {
			'en': 'English',
			'zh-CN': '简体中文',
		};
		return names[locale] || locale;
	}
}

// Singleton instance
let i18nInstance: I18nManager | null = null;

/**
 * Gets the I18nManager singleton instance
 */
export function getI18n(): I18nManager {
	if (!i18nInstance) {
		i18nInstance = new I18nManager();
	}
	return i18nInstance;
}

/**
 * Convenience function for translating strings
 * @param key - Translation key with dot notation
 * @param params - Optional parameters for string interpolation
 * @returns Translated string
 */
export function t(key: string, params?: Record<string, string | number>): string {
	return getI18n().t(key, params);
}

/**
 * Initializes the i18n system with the Sim's locale preference
 * Forces 'zh-CN' as default and saves to localStorage for persistence
 */
export function initI18n(initialLocale?: Locale): I18nManager {
	if (!i18nInstance) {
		i18nInstance = new I18nManager({ locale: initialLocale });
		// Save the default locale to localStorage for persistence
		if (!localStorage.getItem(STORAGE_KEY)) {
			localStorage.setItem(STORAGE_KEY, i18nInstance.getLocale());
		}
		console.log('i18n initialized with locale:', i18nInstance.getLocale());
	}
	return i18nInstance;
}
